import React from 'react'
import { TextField, FormControl, Tooltip, Icon, Typography } from '@mui/material'
import { withStyles } from '@mui/styles'

import { textInputStyles } from './text-input.styles'

const ROWS_MAX = 5

/**
 * @typedef {object} TextInputReplaceProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por `withStyles`.
 * @property {'text' | 'email' | 'password' | 'number' | 'tel' | 'url'} [type='text'] - El tipo de input HTML.
 * @property {string} [title] - Título que se muestra encima del input.
 * @property {string} [tooltip] - Texto del tooltip de ayuda.
 * @property {string} [value] - El valor actual del input.
 * @property {string} [units] - Unidades que se muestran al final del valor cuando type es 'text'.
 */

/**
 * Componente de entrada de texto simplificado con variante 'standard' y modo de solo lectura automático.
 * 
 * Versión alternativa de TextInput que:
 * - Usa variante 'standard' en lugar de 'outlined'
 * - Muestra texto estático cuando type es 'text'
 * - Muestra input editable para otros tipos
 * - Incluye soporte para unidades en modo texto
 * 
 * @component
 * @example
 * const MyDisplay = () => {
 *   return (
 *     <TextInputReplace
 *       type="text"
 *       title="Peso"
 *       value="75"
 *       units="kg"
 *       tooltip="Peso del producto"
 *     />
 *   );
 * };
 * 
 * @param {TextInputReplaceProps} props - Las props del componente TextInputReplace.
 */
export const TextInputReplace = React.memo(
  withStyles(textInputStyles)(({ classes, type = 'text', ...props }) => {
    return (
      <FormControl className={classes.root}>
        {props.title ? (
          <div className={classes.title}>
            {props.title}
            {props.tooltip ? (
              <span style={{ fontSize: 16, marginLeft: 4 }}>
                <Tooltip
                  placement="top"
                  title={
                    <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
                      {props.tooltip}
                    </Typography>
                  }
                >
                  <Icon fontSize="inherit">help_outline</Icon>
                </Tooltip>
              </span>
            ) : null}
          </div>
        ) : null}
        {type === 'text' ? (
          <Typography color="textSecondary">
            {props.value ? props.value + (props.units ? ' ' + props.units : '') : '--'}
          </Typography>
        ) : (
          <TextField
            rowsMax={ROWS_MAX}
            {...props}
            type={type}
            variant="standard"
            value={props.value || ''}
            placeholder="--"
            InputProps={{ className: classes.input, ...props.InputProps }}
            // inputProps={{ ...props.InputProps }}
          />
        )}
      </FormControl>
    )
  })
)
